import { useCallback, useEffect, useState } from 'react';
import {
  FiiDiiLatestSnapshot,
  FiiDiiHistoryPayload,
  FiiDiiMonthlyPayload,
} from '../types';
import {
  fetchFiiDiiLatest,
  fetchFiiDiiHistory,
  fetchFiiDiiMonthly,
  FALLBACK_LATEST,
  FALLBACK_HISTORY,
  FALLBACK_MONTHLY,
} from '../services/fiiDiiService';

export interface UseFiiDiiDataResult {
  latest: FiiDiiLatestSnapshot | null;
  history: FiiDiiHistoryPayload | null;
  monthly: FiiDiiMonthlyPayload | null;
  loadingLatest: boolean;
  loadingDetails: boolean;
  error: string | null;
  loadFullDetails: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const useFiiDiiData = (): UseFiiDiiDataResult => {
  const [latest, setLatest] = useState<FiiDiiLatestSnapshot | null>(null);
  const [history, setHistory] = useState<FiiDiiHistoryPayload | null>(null);
  const [monthly, setMonthly] = useState<FiiDiiMonthlyPayload | null>(null);
  const [loadingLatest, setLoadingLatest] = useState<boolean>(true);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadLatest = useCallback(async (forceRefresh = false) => {
    setLoadingLatest(true);
    setError(null);
    try {
      const data = await fetchFiiDiiLatest(forceRefresh);
      setLatest(data);
    } catch (err) {
      console.error('Error fetching latest FII/DII data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load latest FII/DII data');
      setLatest((prev) => prev || FALLBACK_LATEST);
    } finally {
      setLoadingLatest(false);
    }
  }, []);

  const loadFullDetails = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && history && monthly) return;
    setLoadingDetails(true);
    setError(null);
    try {
      const [histData, monthData] = await Promise.all([
        fetchFiiDiiHistory(forceRefresh),
        fetchFiiDiiMonthly(forceRefresh),
      ]);
      setHistory(histData);
      setMonthly(monthData);
    } catch (err) {
      console.error('Error loading full FII/DII details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load FII/DII history');
      setHistory((prev) => prev || FALLBACK_HISTORY);
      setMonthly((prev) => prev || FALLBACK_MONTHLY);
    } finally {
      setLoadingDetails(false);
    }
  }, [history, monthly]);

  useEffect(() => {
    void loadLatest(false);
  }, [loadLatest]);

  const refresh = useCallback(async () => {
    await Promise.all([
      loadLatest(true),
      history || monthly ? loadFullDetails(true) : Promise.resolve(),
    ]);
  }, [loadLatest, loadFullDetails, history, monthly]);

  return {
    latest,
    history,
    monthly,
    loadingLatest,
    loadingDetails,
    error,
    loadFullDetails,
    refresh,
  };
};
